import { Session, SessionService, TProjectId, TScriptId, TSessionId, TUpdateSessionRequest } from '@talon/web/lib/api';
import { ISODateString } from '@talon/web/src/types';
import { makeObservable, observable, runInAction, toJS } from 'mobx';
import { BaseStore } from './baseStore'; // for some dumb reason, these have to be imported individually
import { RootStore } from './rootStore'; // for some dumb reason, these have to be imported individually

export class SessionStore extends BaseStore {
  currentSession: Session | undefined = undefined;
  sessionList: Session[];

  isSaving = false;

  constructor(rootStore: RootStore) {
    super(rootStore);
    this.sessionList = [];
    makeObservable(this, {
      currentSession: observable,
      sessionList: observable,
      isSaving: observable,
    });
  }

  setIsSaving(isSaving: boolean) {
    runInAction(() => (this.isSaving = isSaving));
  }

  getLastSessionDate(): string | undefined {
    return [...this.sessionList].sort((a, b) => ((a.date ?? '') > (b.date ?? '') ? -1 : 1))[0]?.date ?? '';
  }

  getSessionDates = (sessionId?: TSessionId): string[] => {
    return (this.sessionList
      ?.filter((se) => se.id !== sessionId)
      .sort((a, b) => ((a.date ?? '') > (b.date ?? '') ? -1 : 1))
      .map((se) => se.date ?? '') ?? []) as string[];
  };

  setCurrentSession(session?: Session) {
    runInAction(() => (this.currentSession = session));
  }

  setSessionList(sessionList: Session[]) {
    runInAction(() => (this.sessionList = sessionList));
  }

  listSessions = async (projectId: string) => {
    try {
      const { sessions } = await SessionService.listSessions(
        projectId,
        undefined,
        undefined,
        undefined,
        'activities,session_estimate,commercial'
      );
      console.log('setting sessionList', sessions);
      this.setSessionList(sessions);
      return sessions;
    } catch (e) {
      this.handleError(e);
    }
  };

  getSession = async (sessionId: TSessionId, relations = 'activities,session_estimate,commercial') => {
    try {
      const { session } = await SessionService.getSession(sessionId, relations);
      const a = await this.rootStore.sessionActivityStore.listSessionActivities(sessionId);
      if (session) {
        if (a) session.activities = a;
        return session;
      }
    } catch (e) {
      this.handleError(e);
    }
  };

  createSession = async (date: ISODateString, projectId: TProjectId, scriptId?: TScriptId) => {
    if (!projectId || !date) return;
    try {
      this.setIsSaving(true);
      const project = this.rootStore.projectStore.projectList?.find((p) => p.id == projectId);
      if (!project) return;
      const { session } = await SessionService.createSession({
        date,
        projectId,
        scriptId,
        isUnion: project.is_union,
      });
      const newSession = await this.getSession(session?.id);
      if (newSession) {
        runInAction(() => {
          this.sessionList.unshift(newSession);
        });
        return newSession;
      }
    } catch (e) {
      this.handleError(e);
      this.setIsSaving(false);
    }
  };

  updateSession = async (sessionId: TSessionId, update: TUpdateSessionRequest) => {
    const i = this.sessionList?.findIndex(({ id }) => id === sessionId);
    console.log(sessionId, toJS(this.sessionList));
    if (i === undefined || i === -1) return;
    try {
      this.setIsSaving(true);
      const { session } = await SessionService.updateSession(sessionId, update);
      const updatedSession = await this.getSession(session.id);
      if (updatedSession) {
        runInAction(() => {
          this.sessionList.splice(i, 1, updatedSession);
        });
        return updatedSession;
      }
    } catch (e) {
      this.handleError(e);
      this.setIsSaving(false);
    }
  };

  deleteSession = async (sessionId: TSessionId) => {
    try {
      this.setIsSaving(true);
      await SessionService.deleteSession(sessionId);
      if (sessionId === this.currentSession?.id) this.setCurrentSession(undefined);
      runInAction(() => {
        this.sessionList = this.sessionList?.filter((p) => p.id !== sessionId);
      });
    } catch (e) {
      this.handleError(e);
      this.setIsSaving(false);
    }
  };

  generateSessionReport = async (sessionId: TSessionId) => {
    try {
      // Type assertion since the API client will be regenerated
      const response = await SessionService.generateSessionReport(sessionId);

      if (response) {
        // Convert base64 to blob
        const byteArray = Uint8Array.from(Buffer.from(response, 'base64'));
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Open PDF in new window
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');

        // Clean up the URL after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);

        return blob;
      }
    } catch (e) {
      this.handleError(e);
    }
  };
}
